/**
 * How reachable a system is from untrusted networks, as one vocabulary.
 *
 * Pure on purpose, so an aggregate can classify exposure without pulling a React module in to do it.
 * The org-level roll-up (`org-threat-model/org-threat-posture.ts`) needs that, and it is why a second
 * definition grew there first — a bare `Set(['yes', 'partial'])` that disagreed with this one about
 * unrecognised values.
 *
 * Both consumers are now wired to this vocabulary: the repository's risk score weights by
 * `coerceInternetFacing`, and the estate's exposure figures count with `isReachableFromUntrusted`. One
 * vocabulary, so the estate figure and the repository chip cannot answer differently.
 */

/**
 * The categorical fields arrive as free text, not enums.
 *
 * `api-schemas.ts` types `trust_level`, `relevance` and `internet_facing` as `z.string()` on purpose:
 * "the values are generated per model and the UI degrades gracefully on ones it does not recognise".
 * Callers therefore normalise and fall back rather than indexing a recipe with an unknown key, which
 * would render an unstyled tag. The fallbacks are the least-committal value in each vocabulary — an
 * unrecognised value must never read as a stronger claim than the model made.
 *
 * The trust-level and relevance coercers in `threat-model-report.tsx` import this too, bucketing their
 * own vocabularies the same way. They held a verbatim copy until recently — the duplication this module
 * exists to prevent, one level down.
 */
export const normalise = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')

/**
 * Ordered most to least exposed, then `unknown`, the way `trust-levels.ts` orders its ramp: anything
 * presenting these as a series — a composition bar, a legend — can map over this and get a meaningful
 * order for free rather than hand-sorting a copy that later disagrees.
 */
export const INTERNET_FACING = ['yes', 'partial', 'no', 'unknown'] as const
export type InternetFacing = (typeof INTERNET_FACING)[number]

export const coerceInternetFacing = (value: string): InternetFacing =>
  INTERNET_FACING.find(option => option === normalise(value)) ?? 'unknown'

/**
 * `partial` counts as reachable.
 *
 * The models hedge — a `partial` carries notes like "exposure depends on deployment" — and a figure that
 * counted only `yes` would report a boundary the model never claimed. `unknown` does not count: absence
 * of an answer is not an answer, and treating it as reachable would inflate every exposure figure with
 * repositories nobody has assessed.
 */
const REACHABLE_FROM_UNTRUSTED: readonly InternetFacing[] = ['yes', 'partial']

export const isReachableFromUntrusted = (value: InternetFacing): boolean => REACHABLE_FROM_UNTRUSTED.includes(value)
